/**
 * JOSSE-COLA-T1 — LO QUE ESPERA UNA DECISION DE JOSSE.
 *
 * DE DONDE SALE, y es un problema que Ares nombro primero como suyo (#10825):
 * "Josse me escribio que le dejo documentacion, preguntas y necesidades en el
 * chat y que se le diluyen, porque sigo y sigo copiando. Tiene razon: hoy le
 * mande NUEVE mensajes y le deje SIETE decisiones abiertas."
 *
 * ESTO NO ES UN TABLERO MAS. Un tablero dice como va todo; esta cola dice UNA
 * cosa: que esta parado esperandolo a el. Si no distingue eso, sobra.
 *
 * ============================================================================
 * LO QUE MEDI ANTES DE ESCRIBIR UNA LINEA, y casi me lo pierdo
 * ============================================================================
 *
 * Iba a escribir un contrato para un endpoint inexistente, como manda la regla
 * de la casa. Fui a medir primero y NO hacia falta:
 *
 *   - GET /api/v1/tasks EXISTE. Medido sin credenciales contra produccion y en
 *     pareja, que es lo unico que prueba algo: /api/v1/tasks -> 401 (existe y
 *     esta gateada) mientras /api/v1/nexo-tasks, /api/v1/tareas y una inventada
 *     de la misma forma -> 404. Y el control del metodo, /api/v1/bitacora -> 200.
 *   - Program.cs:397 cablea PostgresTaskRepository, que lee nexo_private.nexo_tasks
 *     (detecta el schema y prefiere ese, que es el de produccion).
 *   - Y TaskSummaryDto TRAE Metadata. O sea que gate_josse y si_no_se_decide
 *     viajan hasta aqui HOY.
 *
 * ============================================================================
 * LO QUE SI FALTA, Y ES UNA LINEA DE LA API
 * ============================================================================
 *
 * No hay filtro por compuerta. GetAllTasks acepta page, pageSize (tope 50),
 * projectId, status, assignee, sortBy y sortOrder. Nada de metadata. Y el
 * /search tampoco sirve: PostgresTaskRepository:1554 y 1577 buscan solo
 * `title ILIKE OR description ILIKE`, asi que buscar "gate_josse" no encuentra
 * nada.
 *
 * EL COSTO, medido: 1693 tareas, 631 abiertas, 18 con compuerta viva. Sin
 * filtro son TRECE peticiones de 50 para encontrar DIECIOCHO filas.
 *
 * EL CONTRATO QUE DEJO PEDIDO, anclado en columnas reales y no inventadas:
 *   GET /api/v1/tasks?gate=josse
 *   -> WHERE metadata->>'gate_josse' IS NOT NULL
 *      AND metadata->>'gate_josse_resuelto' IS NULL
 *      AND status NOT IN ('completed','cancelled')
 *
 * Y ESTO SE ENCIENDE SOLO cuando exista: se pide el filtro PRIMERO y se
 * comprueba si la API lo aplico de verdad -- mirando si lo que volvio trae
 * compuerta --. Si lo aplico, se deja de barrer. Sin re-deploy y sin que nadie
 * tenga que acordarse. Es el patron de PANEL-AUDIOS.
 */
import { ApiBase } from './ApiBase.js';
import { AppConfig } from '../../config.js';
import { BitacoraApiClient } from './BitacoraApiClient.js';

const TAREAS = '/api/v1/tasks';
// LA CASA PERMANENTE DE LAS DECISIONES (BUZON-JOSSE-T1, nexo-api 239).
//
// Medido antes de escribir una linea, y en pareja porque un 401 suelto no
// prueba nada: GET /api/v1/decisiones contesta 401 -- existe y esta gateado --
// mientras /api/v1/verbo-inventado, de la MISMA forma, contesta 404.
const BUZON = '/api/v1/decisiones';
const POR_PAGINA = 50;          // tope duro de la API: pageSize > 50 lo baja a 50
const TOPE_DECISIONES = 200;    // tope de GET /decisiones (limite > 200 vuelve a 50)
/**
 * Cuantas paginas se barren como maximo.
 *
 * 15 x 50 = 750, y hoy hay 631 tareas abiertas: cubre todo con aire. Pero el
 * numero NO es la garantia -- la garantia es que si el barrido se corta, la
 * pantalla lo DICE en vez de presentar una lista recortada como si fuera
 * completa. Es la leccion del LIMIT con ORDER BY: una ausencia en una lista
 * truncada no prueba nada.
 */
const TOPE_PAGINAS = 15;

const texto = (v) => (v === null || v === undefined ? '' : String(v));
const pick = (o, ...claves) => {
  for (const k of claves) if (o && o[k] !== undefined && o[k] !== null) return o[k];
  return null;
};

/** El fallo dice su causa, no "algo salio mal". */
function motivoDe(res) {
  const txt = JSON.stringify(res?.error ?? '') + ' ' + texto(res?.code) + ' ' + JSON.stringify(res?.details ?? '');
  if (/401|unauthor|sesion|token/i.test(txt)) {
    return { motivo: 'SIN_SESION', detalle: 'tu sesion no alcanza para leer las tareas' };
  }
  if (/403|forbidden/i.test(txt)) {
    return { motivo: 'PROHIBIDO', detalle: 'esta pantalla es del dueno y de los devs' };
  }
  if (/404|not\s*found/i.test(txt)) {
    return { motivo: 'NO_PUBLICADO', detalle: 'esta API todavia no publica las tareas' };
  }
  if (AppConfig.env === 'dev') {
    return { motivo: 'DEV_SIN_API', detalle: 'el panel esta en dev y apunta a ' + AppConfig.apiBase + '; en produccion las tareas si existen (medido: 401, no 404)' };
  }
  return { motivo: 'ERROR_API', detalle: (typeof res?.error === 'string' ? res.error : res?.error?.message) || 'no pudimos hablar con la API ahora' };
}

/** ¿Esta tarea espera de verdad una decision de Josse, y no una ya tomada? */
function esperaDecision(m) {
  if (!m) return false;
  const gate = pick(m, 'gate_josse', 'gateJosse');
  if (gate === null || gate === false || gate === '' || gate === 'false') return false;
  // Una compuerta ya resuelta NO vuelve a la cola. Lo digo con nombre y fecha
  // porque me paso: ARC-CTX-T3 tenia escrito "CONCEDIDO por Josse" en su propia
  // compuerta y seguia contando como pendiente. La primera lista que Josse
  // abriera le habria pedido decidir algo decidido doce horas antes.
  if (pick(m, 'gate_josse_resuelto', 'gateJosseResuelto') !== null) return false;
  return true;
}

/**
 * Una fila de la cola, con los nombres que usa la vista.
 *
 * `pregunta` puede venir como el booleano `true`: eso NO es una pregunta, es una
 * marca. Se distingue a proposito, porque la pantalla tiene que poder decir
 * "esta tarea te espera pero nadie escribio que te pregunta" en vez de mostrar
 * un renglon vacio y que parezca un fallo de la pantalla.
 */
function filaDe(t) {
  const m = pick(t, 'metadata', 'Metadata') || {};
  const gate = pick(m, 'gate_josse', 'gateJosse');
  const esMarca = gate === true || gate === 'true';
  return {
    id: texto(pick(t, 'id', 'Id')),
    titulo: texto(pick(t, 'title', 'Title')),
    estado: texto(pick(t, 'status', 'Status')),
    prioridad: texto(pick(t, 'priority', 'Priority')).toLowerCase(),
    dueno: texto(pick(t, 'assignee', 'Assignee')),
    actualizada: texto(pick(t, 'updatedAt', 'updated_at', 'UpdatedAt')),
    codigo: texto(pick(m, 'task_code', 'taskCode')),
    pregunta: esMarca ? '' : texto(gate),

    // ============================================================================
    // TRES CAMPOS, NO CUATRO NOMBRES DE UNA IDEA. Y esto corrige a los dos.
    // ============================================================================
    //
    // Ares pidio "que la cola lea los cuatro nombres, una linea, porque son
    // cuatro nombres para una idea". Fui a medir cuales son de verdad, y NO son
    // una idea: son TRES distintas, y dos de ellas conviven en las MISMAS ocho
    // tareas -- si conviven, no son sinonimos.
    //
    //   costo_nivel .......... alto / medio / bajo. 7 de 18.
    //   costo_de_no_decidir .. que pasa si NO decide. 7, mas 3 con mi nombre
    //                          si_no_se_decide = 8 de 18.
    //   cuanto_le_cuesta ..... cuanto trabajo le cuesta A EL decidirlo. 7 de 18.
    //                          Ej: "elegir entre tres opciones ya medidas".
    //
    // FUNDIRLAS EN UNA HABRIA PERDIDO la tercera, que es justamente la que mas
    // sirve a alguien desbordado: no le dice que se rompe, le dice que esto le
    // toma treinta segundos. Un tablero que solo enumera peligros cansa; uno que
    // ademas dice "esto es cortito" se puede vaciar.
    //
    // Y ME CORRIJO A MI MISMO: yo escribi -en el PR #276 y en la bitacora- que
    // la cola NO podia ordenar por costo porque el dato no existia. Existe:
    // costo_nivel, en 7 de 18. Lo que no existia era en el nombre que yo mire.
    // Medi la forma que esperaba en vez de la que hay, otra vez.
    //
    // MI COLA MERGEADA LEE 3 DE 18. Con los nombres reales, 8.
    costoNivel: texto(pick(m, 'costo_nivel', 'costoNivel')).toLowerCase(),
    costo: texto(pick(m, 'costo_de_no_decidir', 'si_no_se_decide', 'costo', 'impacto')),
    esfuerzo: texto(pick(m, 'cuanto_le_cuesta', 'cuantoLeCuesta'))
  };
}

const PESO = { critical: 0, high: 1, medium: 2, low: 3 };
/**
 * El orden por COSTO, que es el que Ares queria y yo dije que no se podia.
 *
 * Las que no traen nivel van al final -- no al medio: mezclarlas dejaria a una
 * "alta" debajo de una sin dato, y eso convierte el orden en ruido. Al final,
 * y la pantalla dice cuantas son.
 */
const NIVEL = { alto: 0, alta: 0, medio: 1, media: 1, bajo: 2, baja: 2 };

export const JosseColaApiClient = {
  /**
   * EL BUZON: las decisiones que viven en su propia tabla.
   *
   * Es la casa permanente que pidio Ares -- "ninguna decision de Josse vive
   * solo en el chat: fila en el buzon o no existe" -- y NO reemplaza a
   * pendientes(): esa saca las que estan colgadas de una tarea. Son dos
   * fuentes distintas y la pantalla las muestra por separado, cada una con su
   * cuenta. Juntarlas en un solo numero seria un numero con dos definiciones,
   * que es como se pierde la confianza en un tablero.
   *
   * FALLA POR CAUSA. "No tienes permiso" y "no hay decisiones" se ven igual en
   * pantalla si uno no los separa, y el segundo es una buena noticia falsa:
   * nadie investiga una buena noticia.
   *
   * SOBRE EL PERMISO, y me corrijo: yo escribi que el endpoint exige el scope
   * decisiones.read. Argy-A fue a medirlo (#13308) y es mas fino que eso: una
   * sesion de panel con rol admin PASA aunque no tenga ningun scope, porque la
   * politica trata aparte a esa clase de sesion. O sea que para Josse esta
   * pantalla nace viva, y el scope solo decide para las demas sesiones.
   *
   * @param {string} estado  'pendiente' por defecto; null trae todas
   * @returns {{ok:true, filas:Array, total:number}|{ok:false, motivo, detalle}}
   */
  async buzon(estado = 'pendiente') {
    const q = [`limite=${POR_PAGINA}`];
    if (estado) q.push(`estado=${encodeURIComponent(estado)}`);
    let res;
    try { res = await ApiBase.get(`${BUZON}?${q.join('&')}`); }
    catch (err) { return { ok: false, motivo: 'SIN_RED', detalle: err?.message || 'no se pudo hablar con la API' }; }
    if (!res.ok) return { ok: false, ...motivoDe(res) };
    // El DTO no lleva JsonPropertyName y la API no fija politica, asi que las
    // claves llegan en camelCase. Aun asi se toleran las dos formas: cuesta una
    // linea y evita que un cambio de politica deje la pantalla en blanco.
    const d = res.data?.data ?? res.data ?? [];
    const filas = Array.isArray(d) ? d : (pick(d, 'items', 'Items') || []);
    return { ok: true, filas: Array.isArray(filas) ? filas : [], total: Array.isArray(filas) ? filas.length : 0 };
  },

  /**
   * TODAS las decisiones del buzon, para el tablero (DECISIONES-ARTEFACTO-T1).
   *
   * Con estado vacio la API no filtra (DecisionesJosseService.ListarAsync: estado null = todas)
   * y el tope es 200. Medido el 12-oct: 102 filas. Si vuelven 200 justas, `truncado` lo dice:
   * una lista cortada no se presenta como completa.
   *
   * @returns {{ok:true, filas:Array, truncado:boolean}|{ok:false, motivo, detalle}}
   */
  async todas() {
    let res;
    try { res = await ApiBase.get(`${BUZON}?estado=&limite=${TOPE_DECISIONES}`); }
    catch (err) { return { ok: false, motivo: 'SIN_RED', detalle: err?.message || 'no se pudo hablar con la API' }; }
    if (!res.ok) return { ok: false, ...motivoDe(res) };
    const d = res.data?.data ?? res.data ?? [];
    const filas = Array.isArray(d) ? d : (pick(d, 'items', 'Items') || []);
    const lista = Array.isArray(filas) ? filas : [];
    return { ok: true, filas: lista, truncado: lista.length >= TOPE_DECISIONES };
  },

  /**
   * Lo que espera decision de Josse.
   *
   * @returns {{ok:true, filas:Array, leidas:number, paginas:number, truncado:boolean, filtroDelServidor:boolean}|{ok:false, motivo, detalle}}
   */
  async pendientes() {
    // 1) SE PIDE EL FILTRO PRIMERO. Si algun dia existe, esto deja de barrer solo.
    let filtroDelServidor = false;
    let acumulado = [];
    let leidas = 0;
    let paginas = 0;
    let truncado = false;

    const traer = async (pagina, conFiltro) => {
      const q = [`page=${pagina}`, `pageSize=${POR_PAGINA}`];
      if (conFiltro) q.push('gate=josse');
      let res;
      try { res = await ApiBase.get(`${TAREAS}?${q.join('&')}`); }
      catch (err) { return { ok: false, motivo: 'SIN_RED', detalle: err?.message || 'no se pudo hablar con la API' }; }
      if (!res.ok) return { ok: false, ...motivoDe(res) };
      const d = res.data?.data ?? res.data ?? {};
      const items = pick(d, 'items', 'Items') || [];
      return { ok: true, items: Array.isArray(items) ? items : [] };
    };

    const primera = await traer(1, true);
    if (!primera.ok) return primera;
    leidas += primera.items.length;
    paginas = 1;

    // ¿La API APLICO el filtro, o lo ignoro y devolvio todo?
    // Se mide por la CAPACIDAD, no por la forma: si TODO lo que volvio tiene
    // compuerta, el filtro se aplico. Si vino mezclado, no existe y hay que
    // barrer. Un parametro que no se reconoce se ignora en silencio, asi que
    // preguntarle a la API "soportas gate?" no serviria: hay que mirar el
    // resultado.
    const conCompuerta = primera.items.filter((t) => esperaDecision(pick(t, 'metadata', 'Metadata')));
    filtroDelServidor = primera.items.length > 0 && conCompuerta.length === primera.items.length;
    acumulado = conCompuerta;

    if (!filtroDelServidor) {
      // 2) BARRIDO, con tope y diciendo la verdad si se corta.
      acumulado = conCompuerta;
      let pagina = 2;
      let seguir = primera.items.length === POR_PAGINA;
      while (seguir && pagina <= TOPE_PAGINAS) {
        const r = await traer(pagina, false);
        if (!r.ok) return r;
        leidas += r.items.length;
        paginas = pagina;
        acumulado = acumulado.concat(r.items.filter((t) => esperaDecision(pick(t, 'metadata', 'Metadata'))));
        seguir = r.items.length === POR_PAGINA;
        pagina++;
      }
      truncado = seguir;   // se corto por el tope, no porque se acabaran
    }

    const filas = acumulado.map(filaDe).sort((a, b) => {
      // 1) EL COSTO MANDA, cuando esta escrito. Es lo que Ares pedia desde el
      //    principio y lo que yo di por imposible sin haberlo buscado bien.
      const n = (NIVEL[a.costoNivel] ?? 9) - (NIVEL[b.costoNivel] ?? 9);
      if (n !== 0) return n;
      // 2) Entre iguales -- y entre las que no tienen nivel -- manda la
      //    prioridad, y despues la antiguedad.
      const p = (PESO[a.prioridad] ?? 9) - (PESO[b.prioridad] ?? 9);
      if (p !== 0) return p;
      return String(a.actualizada).localeCompare(String(b.actualizada));   // mas vieja primero
    });

    // Cuantas se pudieron ordenar por costo de verdad. La pantalla lo dice: sin
    // esto, un orden a medias se leeria como un orden completo.
    const conNivel = filas.filter((f) => NIVEL[f.costoNivel] !== undefined).length;

    return { ok: true, filas, leidas, paginas, truncado, filtroDelServidor, conNivel };
  },

  /**
   * La decision de Josse, publicada donde el agente parado la va a ver.
   *
   * NO INVENTA CANAL: usa POST /api/v1/bitacora, que ya existe y ya tiene
   * cliente en el panel. El agente bloqueado lee la bitacora en su loop, asi
   * que una entrada [DECISION-JOSSE] con el task_code es exactamente lo que
   * levanta el bloqueo. Sin esto la cola seria un tablero de quejas.
   */
  async decidir({ tarea, decision, nota }) {
    const t = tarea || {};
    const d = texto(decision).trim();
    if (!t.id) return { ok: false, motivo: 'SIN_TAREA', detalle: 'falta la tarea' };
    if (!d) return { ok: false, motivo: 'SIN_DECISION', detalle: 'escribi que decidis antes de publicarlo' };

    const codigo = t.codigo || t.id;
    const titulo = `[DECISION-JOSSE] ${codigo}: ${d}`.slice(0, 150);   // la columna es varchar(200); 150 es el limite de la casa

    const res = await BitacoraApiClient.create({
      origen: 'panel-josse',
      titulo,
      notas: JSON.stringify({
        type: 'josse.decision',
        agent: 'AGT-WIZARD-000 (Josse)',
        target_agent: t.dueno || null,
        task_id: t.id,
        task_code: t.codigo || null,
        tarea: t.titulo,
        LA_PREGUNTA: t.pregunta || '(nadie escribio que se le preguntaba)',
        LA_DECISION: d,
        nota: texto(nota).trim() || null,
        publicado_desde: 'JOSSE-COLA-T1, la cola del panel'
      }),
      payload: { task_id: t.id, decision: d }
    });

    if (!res || res.ok === false) return { ok: false, ...motivoDe(res || {}) };
    return { ok: true, titulo };
  }
};
